import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";
import { BASE_URL } from "../../Const/Url";
import { logout_func2 } from "../../Const/logoutCommon";
import { showToast } from "../../../utils/ToastHelper";
import Loader from "../../CustomCommons/Loader";
import DeleteIcon from "../../CustomCommons/DeleteIcon";
import EditIcon from "../../CustomCommons/EditIcon";
import plus from "../../../assets/Icon/plus.svg";

const HeroBannerList = () => {
  const history = useHistory();
  const [banners, setBanners] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [offset, setOffset] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  const LIMIT = 10;

  const getOffsetFromUrl = (url) => {
    if (!url) return 0;
    try {
      const parsed = new URL(url);
      return Number(parsed.searchParams.get("offset") || 0);
    } catch (error) {
      const queryString = url?.split("?")[1] || "";
      const params = new URLSearchParams(queryString);
      return Number(params.get("offset") || 0);
    }
  };

  const fetchHeroBanners = useCallback(
    (searchText = "", { urlOverride, offsetValue = 0 } = {}) => {
      setIsLoading(true);
      const resolvedOffset =
        typeof offsetValue === "number" && offsetValue >= 0 ? offsetValue : 0;

      let requestUrl = urlOverride;
      if (!requestUrl) {
        const params = new URLSearchParams();
        params.append("limit", LIMIT);
        params.append("offset", resolvedOffset);
        if (searchText) {
          params.append("search", searchText);
        }
        requestUrl = `${BASE_URL}api/v1/frontend/admin/v2/hero-banners/?${params.toString()}`;
      }

      axios
        .get(requestUrl)
        .then((res) => {
          const payload = res?.data?.data || {};
          setBanners(payload?.results || []);
          setTotalData(payload?.count || 0);
          setNextUrl(payload?.next || null);
          setPrevUrl(payload?.previous || null);
          setOffset(resolvedOffset);
          setPageNumber(Math.floor(resolvedOffset / LIMIT) + 1);
        })
        .catch((err) => {
          const message = err?.response?.data?.message;
          const status = err?.response?.status;

          if (message === "Invalid token." || status === 401) {
            logout_func2(() => {}, history);
            return;
          }
          showToast("error", message || "Failed to load hero banners");
        })
        .finally(() => setIsLoading(false));
    },
    [history]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchHeroBanners(query, { offsetValue: 0 });
    }, 400);

    return () => clearTimeout(debounce);
  }, [query, fetchHeroBanners]);

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getMediaTypeLabel = (type) => {
    if (type === 0) return "Image";
    if (type === 1) return "Video";
    return "-";
  };

  const totalPages = LIMIT ? Math.ceil((totalData || 0) / LIMIT) || 1 : 1;

  const handlePaginationPrevious = () => {
    if (!prevUrl) return;
    const derivedOffset = getOffsetFromUrl(prevUrl);
    fetchHeroBanners(query, {
      urlOverride: prevUrl,
      offsetValue: derivedOffset,
    });
  };

  const handlePaginationNext = () => {
    if (!nextUrl) return;
    const derivedOffset = getOffsetFromUrl(nextUrl);
    fetchHeroBanners(query, {
      urlOverride: nextUrl,
      offsetValue: derivedOffset,
    });
  };

  const handleEdit = (item) => {
    const identifier = item?.slug;
    if (!identifier) {
      showToast("error", "Unable to identify banner");
      return;
    }
    history.push(`/website-settings/edit-hero-banner/${identifier}`);
  };

  const handleCreate = () => {
    history.push("/website-settings/create-hero-banner");
  };

  const handleDelete = (item) => {
    const identifier = item?.slug || item?.id;
    if (!identifier) {
      showToast("error", "Unable to identify banner");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this data.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (!willDelete) {
        swal("Your data is safe!");
        return;
      }

      axios
        .delete(
          `${BASE_URL}api/v1/frontend/admin/v2/hero-banners/${identifier}/`
        )
        .then(() => {
          swal("Hero banner deleted!", { icon: "success" });
          showToast("success", "Hero banner deleted successfully");
          fetchHeroBanners(query, { offsetValue: offset });
        })
        .catch((err) => {
          const message = err?.response?.data?.message;
          showToast("error", message || "Failed to delete hero banner");
        });
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent">
      <div className="cotagorey-head mb-4">
        <div>
          <h3>Hero Banner</h3>
          <input
            style={{ color: "black" }}
            type="text"
            className="filter-input"
            placeholder="Search here"
            onChange={(e) => {
              setQuery(e.target?.value);
            }}
          />
        </div>
        <div className="filter_div">
          <div className="button-div" onClick={handleCreate}>
            <img src={plus} alt="Add hero banner" />
            <span>Add Hero Banner</span>
          </div>
        </div>
      </div>

      <div style={{ display: "block", overflowX: "auto" }}>
        <table className="table_my pdf-table my-3">
          <thead
            className="bg-dark text-white head-row mt-3 main-head-print"
            style={{ height: "50px" }}
          >
            <tr>
              <th style={{ width: "100px", textAlign: "center" }}>#</th>
              <th style={{ width: "100px" }}>Image</th>
              <th style={{ minWidth: "220px" }}>Name</th>
              <th style={{ minWidth: "140px" }}>Media Type</th>
              <th style={{ minWidth: "120px" }}>Order</th>
              <th style={{ minWidth: "140px" }}>Currently Active</th>
              <th style={{ minWidth: "140px" }}>Status</th>
              <th style={{ minWidth: "180px" }}>Created At</th>
              <th style={{ width: "140px", textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody className="tb">
            {banners?.length ? (
              banners.map((item, index) => (
                <tr key={item?.id || index} className="table-row">
                  <td className="text-center">{index + 1}</td>
                  <td>
                    {item?.media_type === 0 ? (
                      <img
                        style={{
                          width: "50px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                        src={item?.image_details?.doc_url}
                        alt="Hero Banner"
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f0f0f0",
                          borderRadius: "4px",
                        }}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M8 5V19L19 12L8 5Z" fill="#666" />
                        </svg>
                      </div>
                    )}
                  </td>
                  <td>{item?.name || "-"}</td>
                  <td>{getMediaTypeLabel(item?.media_type)}</td>
                  <td>{item?.order ?? "-"}</td>
                  <td>
                    {item?.is_currently_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>
                  <td>
                    {item?.is_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </td>
                  <td>{formatDate(item?.created_at)}</td>
                  <td className="action">
                    <div className="d-flex justify-content-center gap-3">
                      <div
                        className="edit-del green"
                        onClick={() => handleEdit(item)}
                      >
                        <EditIcon />
                      </div>
                      <div
                        className="edit-del red"
                        onClick={() => handleDelete(item)}
                      >
                        <DeleteIcon />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8">
                  <div className="not_found d-flex justify-content-center align-items-center py-4">
                    <h4 className="my-4">No Data Found</h4>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalData > 0 && (
        <div className="row pag" style={{ marginTop: "30px" }}>
          <div className="col-12 col-md-4">
            <p className="inter" style={{ color: "#AEAEB2", fontSize: "16px" }}>
              Total Entries:{" "}
              <span style={{ color: "black", fontSize: "14px" }}>
                {totalData}
              </span>
            </p>
          </div>
          {totalPages > 1 && (
            <div className="col-12 col-md-8 d-flex justify-content-md-end">
              <div className="pagination-controls d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationPrevious}
                  disabled={!prevUrl}
                >
                  Prev
                </button>
                <span className="d-flex align-items-center">
                  Page {pageNumber} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationNext}
                  disabled={!nextUrl}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeroBannerList;
